import type {
  BackendClient,
  TeammateRealtimeStatsEvent,
} from '@playbooks/core/src/services/BackendClient'

export interface RealtimeUsage {
  total_tokens?: number
  input_tokens?: number
  output_tokens?: number
  input_token_details?: {
    text_tokens?: number
    audio_tokens?: number
    image_tokens?: number
    cached_tokens?: number
    cached_tokens_details?: {
      text_tokens?: number
      audio_tokens?: number
      image_tokens?: number
    }
  }
  output_token_details?: {
    text_tokens?: number
    audio_tokens?: number
  }
}

export interface RealtimeStatsSourceEvent {
  type: string
  response?: {
    id?: string
    usage?: RealtimeUsage
  }
}

const DEFAULT_FLUSH_INTERVAL_MS = 30000
const MAX_EVENTS_PER_UPLOAD = 500

/**
 * Collects OpenAI Realtime `response.done` usage events and uploads them in batches.
 */
export class RealtimeStatsCollector {
  private sessionId: number | undefined
  private nextSequence = 1
  private pendingEvents: TeammateRealtimeStatsEvent[] = []
  private flushTimer: ReturnType<typeof setInterval> | undefined
  private flushPromise: Promise<void> | undefined

  constructor(private readonly backendClient: BackendClient) {}

  /**
   * Start collecting stats for a newly-created teammate session.
   */
  start(sessionId: number) {
    this.stopTimer()
    this.sessionId = sessionId
    this.nextSequence = 1
    this.pendingEvents = []
    this.flushPromise = undefined
    this.flushTimer = setInterval(() => {
      void this.flush()
    }, DEFAULT_FLUSH_INTERVAL_MS)
  }

  /**
   * Record a Realtime server event if it contains response usage.
   */
  record(event: RealtimeStatsSourceEvent) {
    if (!this.sessionId || event.type !== 'response.done' || !event.response?.usage) return

    const sequence = this.nextSequence++
    this.pendingEvents.push({
      sequence,
      eventAt: new Date().toISOString(),
      responseId: event.response.id,
      usage: toUsageStats(event.response.usage),
    })
  }

  /**
   * Flush pending events and mark the session as ended.
   */
  async stop() {
    this.stopTimer()
    await this.flush(true)
    this.sessionId = undefined
  }

  /**
   * Upload pending events. Failed batches are kept and retried on the next flush.
   */
  async flush(isFinal = false) {
    const sessionId = this.sessionId
    if (!sessionId) return
    if (this.flushPromise) await this.flushPromise

    const events = this.pendingEvents
    this.pendingEvents = []

    if (events.length === 0 && !isFinal) return

    this.flushPromise = this.uploadEvents(sessionId, events, isFinal)
      .finally(() => {
        this.flushPromise = undefined
      })

    await this.flushPromise
  }

  private async uploadEvents(sessionId: number, events: TeammateRealtimeStatsEvent[], isFinal: boolean) {
    for (let index = 0; index < events.length || (index === 0 && isFinal); index += MAX_EVENTS_PER_UPLOAD) {
      const chunk = events.slice(index, index + MAX_EVENTS_PER_UPLOAD)
      const isLastChunk = index + MAX_EVENTS_PER_UPLOAD >= events.length

      try {
        await this.backendClient.updateTeammateSessionStats(sessionId, {
          events: chunk,
          isFinal: isFinal && isLastChunk,
        })
      } catch (error) {
        // Keep the failed chunk and all unsent events for the next scheduled retry.
        this.pendingEvents = [...chunk, ...events.slice(index + MAX_EVENTS_PER_UPLOAD), ...this.pendingEvents]
        console.error('Failed to upload AI Teammate Realtime stats:', error)
        return
      }
    }
  }

  private stopTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer)
      this.flushTimer = undefined
    }
  }
}

function toUsageStats(usage: RealtimeUsage): TeammateRealtimeStatsEvent['usage'] {
  return {
    totalTokens: usage.total_tokens,
    inputTokens: usage.input_tokens,
    outputTokens: usage.output_tokens,
    inputTextTokens: usage.input_token_details?.text_tokens,
    inputAudioTokens: usage.input_token_details?.audio_tokens,
    cachedInputTokens: usage.input_token_details?.cached_tokens,
    cachedTextTokens: usage.input_token_details?.cached_tokens_details?.text_tokens,
    cachedAudioTokens: usage.input_token_details?.cached_tokens_details?.audio_tokens,
    outputTextTokens: usage.output_token_details?.text_tokens,
    outputAudioTokens: usage.output_token_details?.audio_tokens,
  }
}
